import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import { SearchRequest, QuestionGetAnswersRequest, QuestionSaveRequest, QuestionTranslationSaveRequest, QuestionTranslationDeleteRequest } from '../../services/api/interfaces/ApiRequest'
import { questionList, questionGetAnswers, questionSave, questionTranslationSave, questionTranslationDelete } from '../../services/api'
import IQuestion from '../../interfaces/IQuestion'

interface QuestionContextState extends BaseContextState {
  questions: IQuestion[] | null,
  question: IQuestion | null,
  answers: any | null
  filters?: any
}

export interface QuestionContextData extends QuestionContextState {
  resetErrors: Function,
  requestQuestionList: (args: SearchRequest) => Promise<any>,
  requestQuestionFind: (args: SearchRequest) => Promise<IQuestion | null>,
  requestQuestionGetAnswers: (args: QuestionGetAnswersRequest) => Promise<any>,
  requestQuestionSave: (args: QuestionSaveRequest) => Promise<IQuestion>,
  requestQuestionTranslationSave: (args: QuestionTranslationSaveRequest) => Promise<IQuestion>,
  requestQuestionTranslationDelete: (args: QuestionTranslationDeleteRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as QuestionContextData)

// Export consumer
export const QuestionConsumer = Consumer

export class QuestionProvider extends BaseContextProvider {
  state: QuestionContextState = {
    questions: null,
    question: null,
    answers: null
  }

  /**
   * Make api request for question list
   * 
   * @param {SearchRequest} payload 
   */
  requestQuestionList = (payload: SearchRequest) => {
    this.setState({filters: payload})
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        question: null
      }, async () => {
        const response = await questionList(payload)
  
        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'questions'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for question finder
   * 
   * @param {SearchRequest} payload 
   */
  requestQuestionFind = (payload: SearchRequest): Promise<IQuestion | null> => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        question: null,
        answers: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
        const response = await questionList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'question'
          },
        })

        if (this.state.success) {
          resolve(this.state.question)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for question answers list
   * 
   * @param {QuestionGetAnswersRequest} payload 
   */
  requestQuestionGetAnswers = (payload: QuestionGetAnswersRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        answers: null
      }, async () => {
        const response = await questionGetAnswers(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            origin: 'question_answers',
            destination: 'answers'
          },
        })

        if (this.state.success) {
          resolve(this.state.answers)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for question save
   * 
   * @param {QuestionSaveRequest} payload 
   */
  requestQuestionSave = (payload: QuestionSaveRequest): Promise<IQuestion> => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        question: null
      }, async () => {
        const response = await questionSave(payload)
  
        // Process our response
        this.processResponse(response, {
          keepFetching: true,
          mappings: [
            { origin: 'organization_question', destination: 'question' }
          ]
        })
  
        // Get answers which were created now
        if (this.state.success && this.state.question) {
          await this.requestQuestionGetAnswers({
            question_uuid: this.state.question.uuid
          })

          resolve(this.state.question)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for question translation save
   * 
   * @param {QuestionTranslationSaveRequest} payload 
   */
  requestQuestionTranslationSave = (payload: QuestionTranslationSaveRequest): Promise<IQuestion> => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await questionTranslationSave(payload)
  
        // Process our response
        this.processResponse(response, {
          keepFetching: true,
          mappings: [
            { origin: 'organization_question', destination: 'question' }
          ]
        })

        // Get answers which were created now
        if (this.state.success && this.state.question) {
          await this.requestQuestionGetAnswers({
            question_uuid: this.state.question.uuid
          })

          resolve(this.state.question)
        } else {
          reject(this.state.error)
        }
      })
    })  
  }

  /**
   * Make api request for question translation delete
   * 
   * @param {QuestionTranslationDeleteRequest} payload 
   */
  requestQuestionTranslationDelete = (payload: QuestionTranslationDeleteRequest): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await questionTranslationDelete(payload)
  
        // Process our response
        this.processResponse(response, {
          keepFetching: true
        })

        // Get answers which were created now
        if (this.state.success && this.state.question) {
          resolve(this.state.question)
        } else {
          reject(this.state.error)
        }
      })
    })  
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestQuestionList: this.requestQuestionList,
          requestQuestionFind: this.requestQuestionFind,
          requestQuestionGetAnswers: this.requestQuestionGetAnswers,
          requestQuestionSave: this.requestQuestionSave,
          requestQuestionTranslationSave: this.requestQuestionTranslationSave,
          requestQuestionTranslationDelete: this.requestQuestionTranslationDelete
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
